import {booleanAttribute, Component, computed, input, numberAttribute, output} from '@angular/core';
import {MatTooltip} from '@angular/material/tooltip';
import {formatDuration} from '../pipes/format';

export interface HoursDay {
    /** "ma 12" */
    label: string;
    /** yyyy-MM-dd */
    date?: string;
    /** Registered minutes; null or 0 when nothing is registered. */
    minutes: number | null;
    today?: boolean;
    weekend?: boolean;
    future?: boolean;
    /** Absent (leave): no red "0:00" for an empty day. */
    absent?: boolean;
    /** Background of the cell, e.g. the leave color as a gradient over the working day. */
    background?: string | null;
    /** Tooltip, e.g. the leave type and time. */
    tooltip?: string;
}

/** Below this the bar turns orange (README open point 4: werkdag < 6 uur). */
const SHORT_DAY_MINUTES = 6 * 60;
/** The bar is full at 9 hours. */
const FULL_DAY_MINUTES = 9 * 60;

/**
 * One week of registered hours: "Week 42  12–18 okt   15:00 / 40:00" and a bar per day with the hours in it
 * (soft green, orange under 6 hours; red "0:00" for a past working day without hours).
 */
@Component({
    selector: 'app-hours-week',
    imports: [MatTooltip],
    templateUrl: './hours-week.component.html',
    styleUrl: './hours-week.component.scss',
    host: {
        '[class.small]': 'size() === "sm"',
        '[attr.title]': 'null'
    }
})
export class HoursWeekComponent {
    /** "Week 42" */
    readonly title = input<string>('');
    /** "12–18 okt" */
    readonly range = input<string>('');
    readonly days = input<HoursDay[]>([]);
    /** Week total in minutes; defaults to the sum of the days. */
    readonly totalMinutes = input<number | null>(null);
    readonly targetMinutes = input(40 * 60, {transform: numberAttribute});
    readonly size = input<'md' | 'sm'>('md');
    /** Double-clicking a day emits `dayDblClick` (e.g. to open Urenregistratie on that day). */
    readonly openableDays = input(false, {transform: booleanAttribute});
    readonly dayDblClick = output<HoursDay>();

    readonly total = computed(() => formatDuration(this.totalMinutes() ?? this.days().reduce((sum, day) => sum + (day.minutes ?? 0), 0)));
    readonly target = computed(() => formatDuration(this.targetMinutes()));
    readonly view = computed(() => this.days().map(day => {
        const minutes = day.minutes ?? 0;
        const missing = !minutes && !day.future && !day.weekend && !day.absent;
        return {
            ...day,
            height: Math.min(100, minutes / FULL_DAY_MINUTES * 100),
            short: minutes > 0 && minutes < SHORT_DAY_MINUTES,
            value: minutes ? formatDuration(minutes) : (missing ? '0:00' : ''),
            missing
        };
    }));
}
